/**
 * PercentStepperInput.tsx
 * The UNIFORM "this project" % editor for the split availability bar (redesign
 * 2026-07-14, SPEC.md §2): `− [ typeable number input ] +`, fixed step 20% (no
 * 5/25 toggle), and pressing + on a 0% cell jumps straight to 100% (the
 * "don't make users set a % every week" default). The input is also directly
 * TYPEABLE for any custom %.
 *
 * Presentational — the parent commits the value (RosterWeekCell → the roster
 * PUT). Colors via theme tokens — no hex literals (CODING-STANDARDS).
 */

import React, { useEffect, useState } from 'react';
import { Box, IconButton, TextField } from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from '@mui/material/styles';
import { clampTypedPercent, nextStepDown, nextStepUp } from './capacitySplitModel';

export interface PercentStepperInputProps {
  /** Current committed value (from the parent's model — the source of truth). */
  value: number;
  disabled?: boolean;
  onChange: (nextPercent: number) => void;
}

export const PercentStepperInput: React.FC<PercentStepperInputProps> = ({ value, disabled = false, onChange }) => {
  const theme = useTheme();
  const [text, setText] = useState(String(value));
  const [focused, setFocused] = useState(false);

  // Re-sync the typed text from the committed value, but never while the user
  // is actively typing (would clobber a half-typed number mid-edit).
  useEffect(() => {
    if (!focused) setText(String(value));
  }, [value, focused]);

  const commit = () => {
    const parsed = clampTypedPercent(Number(text));
    setText(String(parsed));
    if (parsed !== value) onChange(parsed);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, width: 'fit-content', mx: 'auto' }}>
      <IconButton
        size="small"
        disabled={disabled || value <= 0}
        onClick={() => onChange(nextStepDown(value))}
        aria-label="Reduce this project's share"
        sx={{ p: 0.2, color: theme.schedule.machineTime }}
      >
        <RemoveIcon sx={{ fontSize: 14 }} />
      </IconButton>

      <TextField
        size="small"
        type="number"
        value={text}
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => { setFocused(false); commit(); }}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        inputProps={{ 'aria-label': "This project's % of the week", style: { textAlign: 'center', padding: '2px 0' } }}
        sx={{
          width: 52,
          mx: 0.25,
          '& .MuiOutlinedInput-root': { fontSize: '0.74rem', fontWeight: 800, color: theme.schedule.machineTime },
          '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
          '& input[type=number]': { MozAppearance: 'textfield' },
        }}
      />

      <IconButton
        size="small"
        disabled={disabled}
        onClick={() => onChange(nextStepUp(value))}
        aria-label="Increase this project's share"
        sx={{ p: 0.2, color: theme.schedule.machineTime }}
      >
        <AddIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
};
